









import { Component, Prop, Vue } from "@/shared/vuepack";
import { GenericBarChartDataPoint, GenericChartValueType } from "@/shared/types";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";
import { formatCurrency } from "@/shared/currency";

@Component({
  components: {
    GChart
  },
})
export default class GenericBarChart extends Vue {
  @Prop({ default: 220 })
  height: number;

  @Prop({ required: false })
  vAxisTitle: string;

  @Prop({ required: false })
  hAxisTitle: string;

  @Prop({ required: true })
  valueLabel: string;

  @Prop({ required: true })
  valueType: GenericChartValueType;

  @Prop({ required: false })
  dateTooltipLabel: string;

  @Prop({ required: true })
  dateFormatString: string;

  @Prop({ required: true })
  dataSet: GenericBarChartDataPoint[];

  themeStore = useThemeStore();

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      chartArea: {
        top: 15,
        height: "80%"
      },
      color: this.themeStore.tertiaryColor,
      tooltip: {
        isHtml: true,
      },
      hAxis: {
        title: this.hAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        gridlines: {
          color: "transparent"
        },
        slantedText: false,
        baselineColor: "black",
      },
      vAxis: {
        title: this.vAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        baselineColor: "black",
        format: this.vAxisFormat,
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  get vAxisFormat (): string {
    if (this.valueType === GenericChartValueType.Percentage) {
      return `#'%'`;
    } else if (this.valueType === GenericChartValueType.Dollar) {
      return "currency";
    }

    return "##";
  }

  formattedValueForTooltip (value: number): string {
    if (this.valueType === GenericChartValueType.Dollar) {
      return formatCurrency(value);
    } else if (this.valueType === GenericChartValueType.Percentage) {
      return `${value.toFixed(2)}%`;
    }

    return `${value}`;
  }

  tooltipFormatter (data: GenericBarChartDataPoint): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${data.label}
                        </div>
                        <div margin-bottom: 3px;">
                          ${this.valueLabel}:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${this.formattedValueForTooltip(data.value)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartDataItem (chartPoint: GenericBarChartDataPoint): any {
    return [
      chartPoint.label,
      chartPoint.value,
      `stroke-color: ${this.themeStore.tertiaryColor}; color: ${this.themeStore.tertiaryColor};`,
      this.tooltipFormatter(chartPoint),
    ];
  }

  get formattedChartData (): any {
    const formattedChartPoints = this.dataSet.map(this.formatChartDataItem);

    return [
      [
        this.hAxisTitle,
        this.vAxisTitle,
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
      ],
      ...formattedChartPoints
    ];
  }
}
